import React, { useReducer, useCallback, useMemo, ReactNode, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { isDev } from 'helpers/helper';
import { UserContext } from './user/UserContext';
import { logoutMutation } from 'services/auth';
import {
  AuthContext,
  authReducer,
  initialState,
  LOGIN_MUTATION,
  REGISTER_MUTATION,
} from './AuthContextUtils';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const userContext = useContext(UserContext);
  const history = useHistory();
  const logoutRedirectUrl = import.meta.env.VITE_HOME_URL || (isDev ? '/login' : '/page/about');

  const [loginMutation] = useMutation(LOGIN_MUTATION);
  const [registerMutation] = useMutation(REGISTER_MUTATION);

  const login = useCallback(async (variables: any) => {
    dispatch({ type: 'AUTH_REQUEST' });
    try {
      const result = await loginMutation({ variables });
      await userContext.refetchCurrentUser();
      dispatch({ type: 'AUTH_SUCCESS' });
      return result;
    } catch (err: any) {
      const errorMessage = err.message || 'Login failed.';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      throw err;
    }
  }, [loginMutation, userContext]);

  const register = useCallback(async (variables: any) => {
    dispatch({ type: 'AUTH_REQUEST' });
    try {
      const result = await registerMutation({ variables });
      dispatch({ type: 'AUTH_SUCCESS' });
      return result;
    } catch (err: any) {
      const errorMessage = err.message || 'Registration failed.';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      throw err;
    }
  }, [registerMutation]);

  const logout = useCallback(async () => {
    dispatch({ type: 'AUTH_REQUEST' });
    try {
      await logoutMutation();
      await userContext.refetchCurrentUser();
      dispatch({ type: 'AUTH_SUCCESS' });
      history.push('/logout');
      window.location.href = logoutRedirectUrl;
    } catch (err: any) {
      const errorMessage = err.message || 'Logout failed.';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
    }
  }, [userContext, history, logoutRedirectUrl]);

  const value = useMemo(() => ({
    ...state,
    login,
    logout,
    register,
  }), [state, login, logout, register]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
